import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { tenantApi, receiptApi } from '../../services/api';
import { format, addMonths, endOfMonth, startOfMonth } from 'date-fns';
import { formatCurrency } from '../../utils/currency';
import { daysInPeriod, prorationFactor, roundMoney } from '../../utils/prorata';

const ReceiptForm = () => {
  const { tenantId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [tenants, setTenants] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState(null);
  
  const { register, handleSubmit, setValue, formState: { errors }, watch } = useForm({
    defaultValues: {
      tenantId: tenantId || '',
      paymentDate: format(new Date(), 'yyyy-MM-dd'),
      periodStart: format(startOfMonth(new Date()), 'yyyy-MM-dd'),
      periodEnd: format(endOfMonth(new Date()), 'yyyy-MM-dd'),
      paymentType: 'Bank Transfer',
      prorate: false,
      customAmounts: false,
      basicRent: '',
      charges: '',
      notes: ''
    }
  });
  
  const watchTenantId = watch('tenantId');
  const watchPeriodStart = watch('periodStart');
  const watchPeriodEnd = watch('periodEnd');
  const watchProrate = watch('prorate');
  const watchCustomAmounts = watch('customAmounts');
  const watchBasicRent = watch('basicRent');
  const watchCharges = watch('charges');
  
  // Load tenants
  useEffect(() => {
    setLoading(true);
    tenantApi.getAll()
      .then(response => {
        setTenants(response.data);
        if (tenantId && response.data.length > 0) {
          const tenant = response.data.find(t => t.id === parseInt(tenantId));
          if (tenant) {
            setSelectedTenant(tenant);
          }
        }
      })
      .catch(error => {
        toast.error('Failed to load tenants');
        console.error(error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [tenantId]);
  
  // Update selected tenant when tenantId changes
  useEffect(() => {
    if (watchTenantId && tenants.length > 0) {
      const tenant = tenants.find(t => t.id === parseInt(watchTenantId));
      setSelectedTenant(tenant || null);
    } else {
      setSelectedTenant(null);
    }
  }, [watchTenantId, tenants]);

  // Seed the custom amount fields with the tenant's own rent
  useEffect(() => {
    setValue('basicRent', selectedTenant ? String(selectedTenant.basicRent ?? '') : '');
    setValue('charges', selectedTenant ? String(selectedTenant.charges ?? '') : '');
  }, [selectedTenant, setValue]);
  
  // Preview of the amounts the receipt will carry — mirrors the backend, which
  // prorates the tenant's monthly rent over the days actually covered
  const currency = selectedTenant?.currency || 'USD';
  const periodDays = daysInPeriod(watchPeriodStart, watchPeriodEnd);
  const factor = watchProrate ? prorationFactor(watchPeriodStart, watchPeriodEnd) : 1;
  const monthlyBasicRent = watchCustomAmounts
    ? parseFloat(watchBasicRent) || 0
    : selectedTenant?.basicRent || 0;
  const monthlyCharges = watchCustomAmounts
    ? parseFloat(watchCharges) || 0
    : selectedTenant?.charges || 0;
  const previewBasicRent = roundMoney(monthlyBasicRent * factor);
  const previewCharges = roundMoney(monthlyCharges * factor);
  const previewTotal = roundMoney(previewBasicRent + previewCharges);
  const isPartialPeriod = watchProrate && factor > 0 && factor < 0.9999;

  // Set default period for the current month
  const setCurrentMonthPeriod = () => {
    const today = new Date();
    setValue('periodStart', format(startOfMonth(today), 'yyyy-MM-dd'));
    setValue('periodEnd', format(endOfMonth(today), 'yyyy-MM-dd'));
  };
  
  // Set period for the next month
  const setNextMonthPeriod = () => {
    const nextMonth = addMonths(new Date(), 1);
    setValue('periodStart', format(startOfMonth(nextMonth), 'yyyy-MM-dd'));
    setValue('periodEnd', format(endOfMonth(nextMonth), 'yyyy-MM-dd'));
  };
  
  const onSubmit = async (data) => {
    if (!data.tenantId) {
      toast.error('Please select a tenant');
      return;
    }
    
    // Format data for API
    const formattedData = {
      ...data,
      tenantId: parseInt(data.tenantId),
      paymentDate: new Date(data.paymentDate).toISOString(),
      periodStart: new Date(data.periodStart).toISOString(),
      periodEnd: new Date(data.periodEnd).toISOString(),
      prorate: Boolean(data.prorate)
    };

    // Custom amounts override the tenant's monthly rent for this receipt only;
    // otherwise the form's amount fields are not sent at all
    delete formattedData.customAmounts;
    delete formattedData.basicRent;
    delete formattedData.charges;
    if (data.customAmounts) {
      formattedData.basicRent = parseFloat(data.basicRent) || 0;
      formattedData.charges = parseFloat(data.charges) || 0;
    }
    
    setLoading(true);
    try {
      const response = await receiptApi.generate(formattedData);
      toast.success('Receipt generated successfully');
      
      // Ask if user wants to download the receipt
      const downloadReceipt = window.confirm('Receipt generated successfully. Would you like to download it now?');
      if (downloadReceipt) {
        await receiptApi.download(response.data.id);
      }
      
      // Navigate back to the appropriate page
      if (tenantId) {
        navigate(`/tenants/${tenantId}/receipts`);
      } else {
        navigate('/receipts');
      }
    } catch (error) {
      toast.error('Failed to generate receipt');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="card max-w-2xl mx-auto">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Generate Rent Receipt</h1>
      
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="space-y-6">
          {/* Tenant Selection */}
          <div>
            <label htmlFor="tenantId" className="form-label">Tenant</label>
            <select
              id="tenantId"
              className="form-input"
              {...register('tenantId', { required: 'Tenant is required' })}
              disabled={Boolean(tenantId)}
              defaultValue="1"
            >
            { !tenantId &&
              <option value="">Select a tenant</option> }
              {tenants
              .filter(tenant => !tenantId || parseInt(tenantId) === tenant.id)
              .map(tenant => (
                <option key={tenant.id} value={tenant.id}>
                  {tenant.firstName} {tenant.lastName} - {tenant.propertyId}
                </option>
              ))}
            </select>
            {errors.tenantId && <p className="form-error">{errors.tenantId.message}</p>}
          </div>
          
          {/* Selected Tenant Info */}
          {selectedTenant && (
            <div className="bg-gray-50 p-4 rounded border">
              <h3 className="font-medium text-gray-900 mb-2">Tenant Information</h3>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="text-gray-500">Name:</span> {selectedTenant.firstName} {selectedTenant.lastName}
                </div>
                <div>
                  <span className="text-gray-500">Basic Rent:</span> {formatCurrency(selectedTenant.basicRent, currency)}
                </div>
                <div>
                  <span className="text-gray-500">Additional Charges:</span> {formatCurrency(selectedTenant.charges, currency)}
                </div>
                <div>
                  <span className="text-gray-500">Total Rent:</span> {formatCurrency(selectedTenant.totalRent, currency)}
                </div>
              </div>
            </div>
          )}
          
          {/* Payment Information */}
          <div>
            <label htmlFor="paymentDate" className="form-label">Payment Date</label>
            <input
              id="paymentDate"
              type="date"
              className="form-input"
              {...register('paymentDate', { required: 'Payment date is required' })}
            />
            {errors.paymentDate && <p className="form-error">{errors.paymentDate.message}</p>}
          </div>
          
          {/* Rental Period */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="form-label">Rental Period</label>
              <div className="flex space-x-2">
                <button
                  type="button"
                  className="text-xs bg-gray-100 hover:bg-gray-200 text-gray-800 px-2 py-1 rounded"
                  onClick={setCurrentMonthPeriod}
                >
                  Current Month
                </button>
                <button
                  type="button"
                  className="text-xs bg-gray-100 hover:bg-gray-200 text-gray-800 px-2 py-1 rounded"
                  onClick={setNextMonthPeriod}
                >
                  Next Month
                </button>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="periodStart" className="form-label text-xs">Start Date</label>
                <input
                  id="periodStart"
                  type="date"
                  className="form-input"
                  {...register('periodStart', { required: 'Start date is required' })}
                />
                {errors.periodStart && <p className="form-error">{errors.periodStart.message}</p>}
              </div>
              <div>
                <label htmlFor="periodEnd" className="form-label text-xs">End Date</label>
                <input
                  id="periodEnd"
                  type="date"
                  className="form-input"
                  {...register('periodEnd', { required: 'End date is required' })}
                />
                {errors.periodEnd && <p className="form-error">{errors.periodEnd.message}</p>}
              </div>
            </div>

            <div className="mt-3 flex items-start">
              <input
                id="prorate"
                type="checkbox"
                className="mt-1 h-4 w-4 text-primary-600 border-gray-300 rounded"
                {...register('prorate')}
              />
              <label htmlFor="prorate" className="ml-2 text-sm text-gray-700">
                Prorate the rent for a partial period
                <span className="block text-xs text-gray-500">
                  Charges only the days covered by the period, at the monthly rent divided by the number of days in the month.
                </span>
              </label>
            </div>
          </div>

          {/* Custom Amounts */}
          <div>
            <div className="flex items-start">
              <input
                id="customAmounts"
                type="checkbox"
                className="mt-1 h-4 w-4 text-primary-600 border-gray-300 rounded"
                {...register('customAmounts')}
              />
              <label htmlFor="customAmounts" className="ml-2 text-sm text-gray-700">
                Use custom amounts for this receipt
                <span className="block text-xs text-gray-500">
                  Overrides the tenant's rent of {formatCurrency(selectedTenant?.basicRent || 0, currency)} + {formatCurrency(selectedTenant?.charges || 0, currency)}.
                  {watchProrate ? ' These are the full-month amounts, which are then prorated.' : ''}
                </span>
              </label>
            </div>

            {watchCustomAmounts && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
                <div>
                  <label htmlFor="basicRent" className="form-label text-xs">Basic Rent</label>
                  <input
                    id="basicRent"
                    type="number"
                    step="0.01"
                    min="0"
                    className="form-input"
                    {...register('basicRent', {
                      validate: (value) =>
                        !watchCustomAmounts || (value !== '' && parseFloat(value) >= 0) || 'Enter a valid basic rent',
                    })}
                  />
                  {errors.basicRent && <p className="form-error">{errors.basicRent.message}</p>}
                </div>
                <div>
                  <label htmlFor="charges" className="form-label text-xs">Charges</label>
                  <input
                    id="charges"
                    type="number"
                    step="0.01"
                    min="0"
                    className="form-input"
                    {...register('charges', {
                      validate: (value) =>
                        !watchCustomAmounts || (value !== '' && parseFloat(value) >= 0) || 'Enter a valid charges amount',
                    })}
                  />
                  {errors.charges && <p className="form-error">{errors.charges.message}</p>}
                </div>
              </div>
            )}
          </div>

          {/* Amount preview */}
          {selectedTenant && periodDays > 0 && (
            <div className="bg-gray-50 p-4 rounded border">
              <h3 className="font-medium text-gray-900 mb-2">
                Amount ({periodDays} day{periodDays > 1 ? 's' : ''}
                {isPartialPeriod ? `, prorated at ${(factor * 100).toFixed(1)}% of a month` : ''})
              </h3>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="text-gray-500">Basic Rent:</span> {formatCurrency(previewBasicRent, currency)}
                </div>
                <div>
                  <span className="text-gray-500">Charges:</span> {formatCurrency(previewCharges, currency)}
                </div>
                <div className="font-medium">
                  <span className="text-gray-500 font-normal">Total:</span> {formatCurrency(previewTotal, currency)}
                </div>
              </div>
            </div>
          )}
          
          {/* Payment Type */}
          <div>
            <label htmlFor="paymentType" className="form-label">Payment Method</label>
            <select
              id="paymentType"
              className="form-input"
              {...register('paymentType', { required: 'Payment method is required' })}
            >
              <option value="Bank Transfer">Bank Transfer</option>
              <option value="Cash">Cash</option>
              <option value="Check">Check</option>
              <option value="Credit Card">Credit Card</option>
              <option value="Debit Card">Debit Card</option>
              <option value="Online Payment">Online Payment</option>
              <option value="Other">Other</option>
            </select>
            {errors.paymentType && <p className="form-error">{errors.paymentType.message}</p>}
          </div>

          {/* Language */}
          <div>
            <label htmlFor="language" className="form-label">Language</label>
            <select
              id="language"
              className="form-input"
              {...register('language', { required: 'Language is required' })}
              defaultValue="fr"
            >
              <option value="fr">French</option>
              <option value="en">English</option>
            </select>
            {errors.language && <p className="form-error">{errors.language.message}</p>}
          </div>

          {/* Notes */}
          <div>
            <label htmlFor="notes" className="form-label">Notes (Optional)</label>
            <textarea
              id="notes"
              rows="3"
              className="form-input"
              placeholder="Any additional information..."
              {...register('notes')}
            ></textarea>
          </div>
          
          <div className="flex justify-end space-x-4 pt-4">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => tenantId ? navigate(`/tenants/${tenantId}/receipts`) : navigate('/receipts')}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={loading}
            >
              {loading ? 'Generating...' : 'Generate Receipt'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default ReceiptForm;